import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import classNames from 'classnames';

import { actuators as actuatorsRef} from '../../actuators';
import { controllers as controllersRef } from '../../controllers';
import { iconsColor } from '../../controllers';
import { getActuatorNumberOfChannels, getConnectedApplianceIds } from '../../Functions/ActuatorChannels';
import { addPublicURL } from '../../Functions/AddPublicURL';
import { formatEntityId } from '../../Functions/FormatEntityId';
import { getIconPath } from '../../Functions/GetIconPath';
import { getHotelFeatures } from '../../Functions/HotelFeatures';
import { getAccessToken } from '../../Functions/LocalStorageUtils';
import { useCheckIconsConfigurationMutation, useStartIconsConfigurationMutation } from '../../redux/api/configurations';
import { connectApplianceToController, disconnectApplianceFromController, setShowDeviceDetail } from '../../redux/reducers/appReducer';
import { setSelectedDevice, setSelectedDeviceConfiguredImageName, setSelectedDeviceIconsData } from '../../redux/reducers/controllersReducer';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { ConnectedActuatorChannelI, IconsI } from '../../redux/types/controllers';
import Button from '../Button/Button';
import Checkbox from '../Checkbox/Checkbox';
import HotelFeaturesDetail from '../hotelFeaturesDetail/hotelFeaturesDetail';
import LoadingCircle from '../LoadingCircle/LoadingCircle';

import styles from './DeviceDetail.module.scss';

type ChannelListItem = {
    actuatorId: number;
    applianceType: string;
    usage: string;
    channel: number;
    numberOfRequiredConnections: number;
    functionLabels: string[];
    actuatorName: string;
    actuatorType: string;
    representativeIcon: string;
    dragIcon: string;
    applianceIds: number[];
    applianceDetails: {
        id: number;
        label: string;
        icon: string;
    }[];
};

type SlotChannelInfo = {
    actuatorId: number;
    channel: number;
    functionIndex: number;
    functionLabel: string;
    actuatorName: string;
    representativeIcon: string;
    applianceCount: number;
};

type ChannelSortKey = 'id' | 'actuator' | 'type' | 'channel' | 'position' | 'connectedAppliances';
type SortDirection = 'asc' | 'desc';

const DeviceDetail: React.FC = () =>{
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const controllerId = useAppSelector((state) => state.controllers.selectedDevice.id);
    const controllers = useAppSelector((state) => state.controllers.devices);
    const appliances = useAppSelector((state) => state.app.appliances);
    const actuators = useAppSelector((state) => state.app.actuators);
    const controller = useAppSelector((state) => state.controllers.selectedDevice);
    const indicatorColor = iconsColor[controller.color as keyof typeof iconsColor];
    const defaultIconsData = controllersRef.find((item) => item.name === controller.name)?.defaultIconsData;
    const selectedControllerId = controller.id;
    const renderImageName = controller.configuredImageName
        ? `${process.env.REACT_APP_ICONS_URL}/source/userpanels/${controller.configuredImageName}`
        : addPublicURL(`/assets/controllers/render/${controller.renderImage}-${controller.color}.png`);
    const [creatingDesign, setCreatingDesign] = useState(false);
    const [showHotelFeatures, setShowHotelFeatures] = useState(false);
    const [startIconsConfiguration] = useStartIconsConfigurationMutation();
    const [checkIconsConfiguration] = useCheckIconsConfigurationMutation();
    const [showAppliances, setShowAppliances] = useState(true);
    const [showControllerIcons, setShowControllerIcons] = useState(true);
    const [popupPosition, setPopupPosition] = useState<{ top: number, left: number } | null>(null);
    const [editingSlotIndex, setEditingSlotIndex] = useState<number | null>(null);
    const [hoveredChannel, setHoveredChannel] = useState<{ actuatorId: number; channel: number } | null>(null);
    const [channelSort, setChannelSort] = useState<{ key: ChannelSortKey; direction: SortDirection }>({
        key: 'id',
        direction: 'asc'
    });
    const dragPayloadRef = useRef<{
        source: 'channel' | 'controller',
        icon?: string,
        slotIndex?: number,
        actuatorId?: number,
        channel?: number,
        applianceIds?: number[],
        numberOfRequiredConnections?: number,
        functionLabels?: string[]
    } | null>(null);
    const didDropRef = useRef(false);
    const iconWrapperRef = useRef<HTMLDivElement | null>(null);
    const controllerContainerRef = useRef<HTMLDivElement | null>(null);
    const popupRef = useRef<HTMLDivElement | null>(null);

    const getActuatorRequiredConnections = useCallback((actuatorId: number): number => {
        const runtimeActuator = actuators.find((actuator) => actuator.id === actuatorId);
        if (runtimeActuator?.numberOfRequiredConnections !== undefined) {
            return Math.max(1, Math.floor(runtimeActuator.numberOfRequiredConnections));
        }

        const referenceActuator = actuatorsRef.find((actuator) => actuator.type === runtimeActuator?.type);
        if (referenceActuator?.numberOfRequiredConnections !== undefined) {
            return Math.max(1, Math.floor(referenceActuator.numberOfRequiredConnections));
        }

        return 1;
    }, [actuators]);

    const getChannelFunctionLabels = useCallback((requiredConnections: number): string[] => {
        const functionShortcut = t('deviceDetail.channels.functionShort');

        return Array.from(
            { length: Math.max(1, requiredConnections) },
            (_, index) => `${functionShortcut}${index + 1}`
        );
    }, [t]);

    const getSlotApplianceIds = (slot: IconsI): number[] => slot.applianceId ?? [];

    const getSlotLinks = useCallback((slot: IconsI): ConnectedActuatorChannelI[] => {
        const links = (slot.connectedActuatorChannels && slot.connectedActuatorChannels.length > 0)
            ? slot.connectedActuatorChannels
            : (
                slot.connectedActuatorId !== undefined && slot.connectedActuatorChannel !== undefined
                    ? [{ actuatorId: slot.connectedActuatorId, channel: slot.connectedActuatorChannel, functionIndex: 1 }]
                    : []
            );

        return links.filter((link, index, array) =>
            array.findIndex((candidate) =>
                candidate.actuatorId === link.actuatorId &&
                candidate.channel === link.channel &&
                (candidate.functionIndex ?? 1) === (link.functionIndex ?? 1)
            ) === index
        );
    }, []);

    const getApplianceIdsForChannelLink = useCallback((link: ConnectedActuatorChannelI): number[] => {
        const linkedActuator = actuators.find((actuator) => actuator.id === link.actuatorId);
        if (!linkedActuator) {
            return [];
        }

        const channelConnection = (linkedActuator.connectedAppliancesByChannel ?? [])
            .find((connection) => connection.channel === link.channel);
        if (!channelConnection) {
            return [];
        }

        return getConnectedApplianceIds({
            ...linkedActuator,
            connectedAppliancesByChannel: [channelConnection]
        });
    }, [actuators]);

    const getSlotChannelInfos = useCallback((slot: IconsI): SlotChannelInfo[] => {
        const links = getSlotLinks(slot);

        return links
            .map((link) => {
                const linkedActuator = actuators.find((actuator) => actuator.id === link.actuatorId);
                if (!linkedActuator) {
                    return null;
                }

                const channelApplianceIds = getApplianceIdsForChannelLink(link);
                const representativeIcon = addPublicURL(`assets/actuators/${linkedActuator.refImg}`);
                const requiredConnections = getActuatorRequiredConnections(link.actuatorId);
                const functionIndex = link.functionIndex ?? 1;
                const functionLabel = getChannelFunctionLabels(requiredConnections)[functionIndex - 1]
                    ?? `${t('deviceDetail.channels.functionShort')}${functionIndex}`;

                return {
                    actuatorId: link.actuatorId,
                    channel: link.channel,
                    functionIndex,
                    functionLabel,
                    actuatorName: linkedActuator.type,
                    representativeIcon,
                    applianceCount: channelApplianceIds.length
                };
            })
            .filter((info): info is SlotChannelInfo => info !== null);
    }, [actuators, getActuatorRequiredConnections, getApplianceIdsForChannelLink, getChannelFunctionLabels, getSlotLinks, t]);

    const getSlotConnectedCount = useCallback((slot: IconsI): number => {
        const links = getSlotLinks(slot);

        if (links.length > 0) {
            return links.length;
        }

        return slot.applianceId?.length ?? 0;
    }, [getSlotLinks]);

    const channelItems = useMemo(() => {
        const items: ChannelListItem[] = [];

        actuators.forEach((actuator) => {
            const actuatorId = actuator.id;
            if (actuatorId === undefined || actuator.applianceType === 'hvac') {
                return;
            }

            const numberOfChannels = getActuatorNumberOfChannels(actuator);

            Array.from({ length: numberOfChannels }, (_, index) => index + 1).forEach((channelNumber) => {
                const channelConnection = (actuator.connectedAppliancesByChannel ?? [])
                    .find((connection) => connection.channel === channelNumber);

                const channelApplianceIds = channelConnection
                    ? getConnectedApplianceIds({
                        ...actuator,
                        connectedAppliancesByChannel: [channelConnection]
                    })
                    : [];

                const representativeIcon = addPublicURL(`assets/actuators/${actuator.refImg}`);
                const dragIcon = appliances.find((appliance) => channelApplianceIds.includes(appliance.id))?.icon ?? 'add_icon';
                const numberOfRequiredConnections = Math.max(
                    1,
                    Math.floor(
                        actuator.numberOfRequiredConnections
                        ?? actuatorsRef.find((a) => a.type === actuator.type)?.numberOfRequiredConnections
                        ?? 1
                    )
                );
                const functionLabels = getChannelFunctionLabels(numberOfRequiredConnections);
                const applianceDetails = channelApplianceIds
                    .map((applianceId) => appliances.find((appliance) => appliance.id === applianceId))
                    .filter((appliance): appliance is typeof appliances[number] => appliance !== undefined)
                    .map((appliance) => ({
                        id: appliance.id,
                        label: appliance.label,
                        icon: appliance.icon
                    }));
                items.push({
                    actuatorId,
                    channel: channelNumber,
                    actuatorName: actuator.name,
                    actuatorType: actuator.type,
                    applianceType: actuator.applianceType,
                    usage: actuator.usage ?? actuatorsRef.find((a) => a.type === actuator.type)?.usage ?? 'unknown',
                    numberOfRequiredConnections,
                    functionLabels,
                    representativeIcon,
                    dragIcon,
                    applianceIds: channelApplianceIds,
                    applianceDetails
                });
            });
        });

        return items;
    }, [actuators, appliances, getChannelFunctionLabels]);

    const syncApplianceConnections = useCallback((prevIconsData: IconsI[], nextIconsData: IconsI[]) => {
        if(!selectedControllerId){
            return;
        }

        const prevIds = new Set(
            prevIconsData
                .flatMap((slot) => slot.icon !== 'add_icon' ? getSlotApplianceIds(slot) : [])
        );
        const nextIds = new Set(
            nextIconsData
                .flatMap((slot) => slot.icon !== 'add_icon' ? getSlotApplianceIds(slot) : [])
        );

        nextIds.forEach((applianceId) => {
            if(!prevIds.has(applianceId)){
                dispatch(connectApplianceToController({ applianceId, controllerId: selectedControllerId }));
            }
        });
        prevIds.forEach((applianceId) => {
            if(!nextIds.has(applianceId)){
                dispatch(disconnectApplianceFromController({ applianceId, controllerId: selectedControllerId }));
            }
        });
    }, [dispatch, selectedControllerId]);

    useEffect(() => {
        if(controllerId && controllers.length > 0){
            const device = controllers.find((device) => device.id === controllerId);
            if(!device){
                toast.error(t('deviceDetail.noDeviceSelected'));
                dispatch(setShowDeviceDetail(false));
                return;
            }
            dispatch(setSelectedDevice({
                ...device,
                id: device.id,
                name: device.name,
                tech: device.tech,
                type: device.type,
                material: device.material,
                icons: device.icons,
                round: device.round,
                color: device.color,
                colorShortcut: device.colorShortcut,
                className: device.className,
                previewImage: device.previewImage,
                renderImage: device.renderImage,
                configuredImageName: device.configuredImageName,
                state: device.state,
                price: device.price || 0,
                iconsData: device.iconsData,
                connectedActuator: device.connectedActuator,
                thermostatAppliance: device.thermostatAppliance,
                thermostatRequiredActuator: device.thermostatRequiredActuator
            }))
        }else{
            toast.error(t('deviceDetail.noDeviceSelected'));
            dispatch(setShowDeviceDetail(false));
        }
    }, [controllerId, controllers, dispatch, t]);

    useEffect(() => {
        const revertSlotToDefault = (slotIndex: number) => {
            const prevIconsData = controller.iconsData;
            const nextIconsData = controller.iconsData.map((item, index) => {
                if(index !== slotIndex){
                    return item;
                }
                return {
                    ...item,
                    icon: 'add_icon',
                    applianceId: undefined,
                    connectedActuatorChannels: undefined,
                    connectedActuatorId: undefined,
                    connectedActuatorChannel: undefined
                };
            });
            dispatch(setSelectedDeviceIconsData(nextIconsData));
            syncApplianceConnections(prevIconsData, nextIconsData);
        };

        const handleWindowDragOver = (event: DragEvent) => {
            const payload = dragPayloadRef.current;
            if(payload?.source !== 'controller'){
                return;
            }
            event.preventDefault();
        };

        const handleWindowDrop = (event: DragEvent) => {
            const payload = dragPayloadRef.current;
            if(payload?.source !== 'controller' || payload.slotIndex === undefined){
                return;
            }

            event.preventDefault();

            const dropTarget = event.target;
            const droppedOnSlotImg = !!(
                iconWrapperRef.current &&
                dropTarget instanceof HTMLImageElement &&
                iconWrapperRef.current.contains(dropTarget)
            );

            if(!droppedOnSlotImg){
                revertSlotToDefault(payload.slotIndex);
            }

            didDropRef.current = true;

            dragPayloadRef.current = null;
        };

        window.addEventListener('dragover', handleWindowDragOver);
        window.addEventListener('drop', handleWindowDrop);

        return () => {
            window.removeEventListener('dragover', handleWindowDragOver);
            window.removeEventListener('drop', handleWindowDrop);
        };
    }, [controller.iconsData, dispatch, syncApplianceConnections]);

    useEffect(() => {
        if(!creatingDesign) return;

        const interval = window.setInterval(() => {
            void (async () => {
                try {
                    const result = await checkIconsConfiguration().unwrap();
                    if(result.success){
                        dispatch(setSelectedDeviceConfiguredImageName(result.image));
                        setCreatingDesign(false);
                        setShowControllerIcons(true);
                    }
                } catch(error){
                    console.error(error);
                }
            })();
        }, 5000);

        return () => window.clearInterval(interval);
    }, [creatingDesign, checkIconsConfiguration, dispatch]);

    useEffect(() => {
        if(!popupPosition){
            return;
        }

        const handleDocumentMouseDown = (event: MouseEvent) => {
            const target = event.target;
            if(!(target instanceof Element)){
                return;
            }

            if(!controllerContainerRef.current?.contains(target)){
                return;
            }

            if(target.closest('[data-menu-trigger="true"]')){
                return;
            }

            if(popupRef.current?.contains(target)){
                return;
            }

            setPopupPosition(null);
        };

        document.addEventListener('mousedown', handleDocumentMouseDown);

        return () => {
            document.removeEventListener('mousedown', handleDocumentMouseDown);
        };
    }, [popupPosition]);

    const handleChannelDragStart = (item: ChannelListItem) => (event: React.DragEvent<HTMLDivElement>) => {
        didDropRef.current = false;
        dragPayloadRef.current = {
            source: 'channel',
            icon: item.dragIcon,
            actuatorId: item.actuatorId,
            channel: item.channel,
            applianceIds: item.applianceIds,
            numberOfRequiredConnections: item.numberOfRequiredConnections,
            functionLabels: item.functionLabels
        };
        event.dataTransfer.effectAllowed = 'copy';
        event.dataTransfer.setData('application/x-floorplan-icon', JSON.stringify({
            source: 'channel',
            icon: item.dragIcon,
            actuatorId: item.actuatorId,
            channel: item.channel,
            applianceIds: item.applianceIds,
            numberOfRequiredConnections: item.numberOfRequiredConnections,
            functionLabels: item.functionLabels
        }));

        const previewIcon = event.currentTarget.querySelector('img');
        if (previewIcon instanceof HTMLImageElement) {
            const previewSize = previewIcon.getBoundingClientRect();
            event.dataTransfer.setDragImage(
                previewIcon,
                previewSize.width / 2,
                previewSize.height / 2
            );
        }
    };

    const handleControllerIconClick = (event: React.MouseEvent<HTMLDivElement>, position: number) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const popupWidth = 400;
        const popupHeight = 250;
        setEditingSlotIndex(position);

        setPopupPosition({
            top: Math.max(8, rect.top - popupHeight - 8),
            left: Math.max(8, rect.right - popupWidth)
        });
    };

    const handlePopupDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        if(dragPayloadRef.current?.source === 'channel'){
            event.preventDefault();
            event.dataTransfer.dropEffect = 'copy';
        }
    };

    const handlePopupDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();

        if(editingSlotIndex === null){
            return;
        }

        const raw = event.dataTransfer.getData('application/x-floorplan-icon');
        if(!raw){
            return;
        }

        let payload: {
            source: 'channel' | 'controller',
            icon?: string,
            actuatorId?: number,
            channel?: number,
            applianceIds?: number[],
            numberOfRequiredConnections?: number,
            functionLabels?: string[]
        } | null = null;
        try{
            payload = JSON.parse(raw);
        }catch{
            payload = null;
        }

        if(payload?.source !== 'channel' || !payload.icon || payload.channel === undefined || payload.actuatorId === undefined){
            return;
        }

        const payloadActuatorId = payload.actuatorId;
        const payloadChannel = payload.channel;
        const nextApplianceIds = payload.applianceIds ?? [];
        const numberOfRequiredConnections = Math.max(1, Math.floor(payload.numberOfRequiredConnections ?? getActuatorRequiredConnections(payloadActuatorId)));

        const droppedIcon = payload.icon === 'add_icon'
            ? 'add_icon'
            : `controllers/${iconsColor[controller.color as keyof typeof iconsColor]}/${payload.icon}`;
        const prevIconsData = controller.iconsData;

        const editingSlotCurrentLinks = getSlotLinks(prevIconsData[editingSlotIndex]);
        const existingFunctionIndexOnSlot = editingSlotCurrentLinks.find(
            (link) => link.actuatorId === payloadActuatorId && link.channel === payloadChannel
        )?.functionIndex;

        const functionIndex = (() => {
            if (numberOfRequiredConnections <= 1) {
                return 1;
            }

            if (existingFunctionIndexOnSlot !== undefined) {
                return existingFunctionIndexOnSlot;
            }

            const usedFunctionIndexes = prevIconsData
                .flatMap((slot, slotIndex) => {
                    if (slotIndex === editingSlotIndex) {
                        return [] as number[];
                    }

                    return getSlotLinks(slot)
                        .filter((link) => link.actuatorId === payloadActuatorId && link.channel === payloadChannel)
                        .map((link) => link.functionIndex ?? 1);
                });

            for (let idx = 1; idx <= numberOfRequiredConnections; idx += 1) {
                if (!usedFunctionIndexes.includes(idx)) {
                    return idx;
                }
            }

            return 1;
        })();

        const applyLink = (item: IconsI, targetFunctionIndex: number): IconsI => {
            const currentLinks = getSlotLinks(item);
            const nextLink: ConnectedActuatorChannelI = {
                actuatorId: payloadActuatorId,
                channel: payloadChannel,
                functionIndex: targetFunctionIndex
            };

            const baseLinks = currentLinks.filter(
                (link) => !(link.actuatorId === payloadActuatorId && link.channel === payloadChannel && (link.functionIndex ?? 1) === targetFunctionIndex)
            );

            const nextLinks = [...baseLinks, nextLink].filter((link, linkIndex, allLinks) =>
                allLinks.findIndex((candidate) =>
                    candidate.actuatorId === link.actuatorId &&
                    candidate.channel === link.channel &&
                    (candidate.functionIndex ?? 1) === (link.functionIndex ?? 1)
                ) === linkIndex
            );

            const mergedApplianceIds = Array.from(new Set([
                ...(item.applianceId ?? []),
                ...nextApplianceIds
            ]));

            return {
                ...item,
                icon: droppedIcon,
                applianceId: mergedApplianceIds.length > 0 ? mergedApplianceIds : undefined,
                connectedActuatorChannels: nextLinks,
                connectedActuatorId: nextLinks[0]?.actuatorId,
                connectedActuatorChannel: nextLinks[0]?.channel
            };
        };

        // Curtain-style actuators need a second (up/down) function auto-wired to the slot directly below.
        const iconsCount = prevIconsData.length;
        const canAutoPairSlots = numberOfRequiredConnections > 1 && functionIndex === 1 && iconsCount % 2 === 0;
        const partnerSlotIndex = canAutoPairSlots ? (editingSlotIndex + iconsCount / 2) % iconsCount : undefined;
        const partnerAlreadyLinked = partnerSlotIndex !== undefined && getSlotLinks(prevIconsData[partnerSlotIndex]).some(
            (link) => link.actuatorId === payloadActuatorId && link.channel === payloadChannel
        );
        const shouldAutoPairPartner = partnerSlotIndex !== undefined && partnerSlotIndex !== editingSlotIndex && !partnerAlreadyLinked;

        const nextIconsData = prevIconsData.map((item, index) => {
            if (index === editingSlotIndex) {
                return applyLink(item, functionIndex);
            }

            if (shouldAutoPairPartner && index === partnerSlotIndex) {
                return applyLink(item, 2);
            }

            return item;
        });

        dispatch(setSelectedDeviceIconsData(nextIconsData));
        syncApplianceConnections(prevIconsData, nextIconsData);
    };

    const handleChannelUnassign = (slotIndex: number, targetLink: ConnectedActuatorChannelI) => {
        const prevIconsData = controller.iconsData;
        const nextIconsData = controller.iconsData.map((item, index) => {
            if(index !== slotIndex){
                return item;
            }

            const currentLinks = getSlotLinks(item);

            const nextLinks = currentLinks.filter(
                (link) => !(
                    link.actuatorId === targetLink.actuatorId &&
                    link.channel === targetLink.channel &&
                    (link.functionIndex ?? 1) === (targetLink.functionIndex ?? 1)
                )
            );

            if (nextLinks.length === 0) {
                return {
                    ...item,
                    icon: 'add_icon',
                    applianceId: undefined,
                    connectedActuatorChannels: undefined,
                    connectedActuatorId: undefined,
                    connectedActuatorChannel: undefined
                };
            }

            const mergedApplianceIds = Array.from(new Set(
                nextLinks.flatMap((link) => getApplianceIdsForChannelLink(link))
            ));

            return {
                ...item,
                applianceId: mergedApplianceIds.length > 0 ? mergedApplianceIds : undefined,
                connectedActuatorChannels: nextLinks,
                connectedActuatorId: nextLinks[0]?.actuatorId,
                connectedActuatorChannel: nextLinks[0]?.channel
            };
        });
        dispatch(setSelectedDeviceIconsData(nextIconsData));
        syncApplianceConnections(prevIconsData, nextIconsData);
    };

    const editingSlotChannelInfos = editingSlotIndex !== null
        ? getSlotChannelInfos(controller.iconsData[editingSlotIndex])
        : [];

    const controllerButtonCodes = useMemo(() => {
        const matchedController = controllersRef.find((item) => item.name === controller.name);
        if (!matchedController?.buttonCodes) {
            return [] as string[];
        }

        return Object.values(matchedController.buttonCodes) as string[];
    }, [controller.name]);

    const getConnectedPositionsForChannel = useCallback((actuatorId: number, channel: number) => {
        const connectedPositions = new Set<string>();

        controller.iconsData.forEach((slot, slotIndex) => {
            const hasChannelLink = getSlotLinks(slot).some((link) =>
                link.actuatorId === actuatorId && link.channel === channel
            );

            if (!hasChannelLink) {
                return;
            }

            const positionCode = controllerButtonCodes[slotIndex];
            if (positionCode) {
                connectedPositions.add(positionCode);
            }
        });

        if (connectedPositions.size === 0) {
            return '-';
        }

        return Array.from(connectedPositions).join(' / ');
    }, [controller.iconsData, controllerButtonCodes, getSlotLinks]);

    const sortedChannelItems = useMemo(() => {
        const sortedItems = [...channelItems];
        const directionMultiplier = channelSort.direction === 'asc' ? 1 : -1;

        sortedItems.sort((left, right) => {
            switch (channelSort.key) {
                case 'id':
                    return (left.actuatorId - right.actuatorId) * directionMultiplier;
                case 'actuator': {
                    const leftValue = `${left.actuatorType} ${left.actuatorName}`.toLowerCase();
                    const rightValue = `${right.actuatorType} ${right.actuatorName}`.toLowerCase();
                    return leftValue.localeCompare(rightValue) * directionMultiplier;
                }
                case 'type': {
                    const leftValue = `${left.applianceType} ${left.usage}`.toLowerCase();
                    const rightValue = `${right.applianceType} ${right.usage}`.toLowerCase();
                    return leftValue.localeCompare(rightValue) * directionMultiplier;
                }
                case 'channel':
                    return (left.channel - right.channel) * directionMultiplier;
                case 'position': {
                    const leftValue = getConnectedPositionsForChannel(left.actuatorId, left.channel);
                    const rightValue = getConnectedPositionsForChannel(right.actuatorId, right.channel);
                    return leftValue.localeCompare(rightValue) * directionMultiplier;
                }
                case 'connectedAppliances':
                    return (left.applianceIds.length - right.applianceIds.length) * directionMultiplier;
                default:
                    return 0;
            }
        });

        return sortedItems;
    }, [channelItems, channelSort, getConnectedPositionsForChannel]);

    const highlightedControllerSlotIndexes = useMemo(() => {
        if (!hoveredChannel) {
            return new Set<number>();
        }

        const linkedSlotIndexes = new Set<number>();
        controller.iconsData.forEach((slot, slotIndex) => {
            const hasLinkedChannel = getSlotLinks(slot).some((link) =>
                link.actuatorId === hoveredChannel.actuatorId && link.channel === hoveredChannel.channel
            );

            if (hasLinkedChannel) {
                linkedSlotIndexes.add(slotIndex);
            }
        });

        return linkedSlotIndexes;
    }, [controller.iconsData, getSlotLinks, hoveredChannel]);

    const handleChannelSort = (key: ChannelSortKey) => {
        setChannelSort((previous) => {
            if (previous.key === key) {
                return { key, direction: previous.direction === 'asc' ? 'desc' : 'asc' };
            }

            return { key, direction: 'asc' };
        });
    };

    const getSortIndicator = (key: ChannelSortKey) => {
        if (channelSort.key !== key) {
            return '';
        }

        return channelSort.direction === 'asc' ? ' ^' : ' v';
    };

    const handleCreateDesign = async () => {
        if(creatingDesign) return;
        if(!getAccessToken()) return;
        await startIconsConfiguration({
            type: controller.name,
            color: controller.color
        });
        setCreatingDesign(true);
    }
    
    return(
        <>
            {showHotelFeatures && <HotelFeaturesDetail deviceId={controller.id} onClose={() => setShowHotelFeatures(false)} />}
            {creatingDesign && (
                <div className={styles.DeviceDetail__designOverlay}>
                    <div className={styles.DeviceDetail__designOverlay__wrapper}>
                        <h2>{t('deviceDetail.designOverlay.heading')}</h2>
                        <p>{t('deviceDetail.designOverlay.description')}</p>
                        <LoadingCircle size={48} thickness={4} color='primary' className={styles.DeviceDetail__designOverlay__spinner} />
                        <Button variant='fill' size='lg' onClick={() => setCreatingDesign(false)}>{t('common.cancel')}</Button>
                    </div>
                </div>
            )}
            <div id="tutorial-device-detail" className={styles.DeviceDetail}>
                <div className={styles.DeviceDetail__wrapper}>
                    <img src={getIconPath('arrow_back')} alt="Back" onClick={() => dispatch(setShowDeviceDetail(false))} className={styles.DeviceDetail__back} />
                    <h2>{t('deviceDetail.title')}</h2>
                    <p>{getAccessToken() ? t('deviceDetail.description') : t('deviceDetail.description_logout')}</p>
                    <div className={styles.DeviceDetail__container}>
                        <div className={styles.DeviceDetail__container__appliances}>
                            <h3>{t('deviceDetail.channels.heading')}</h3>
                            <div className={styles.DeviceDetail__container__appliances__heading}>
                                <span></span>
                                <button type="button" onClick={() => handleChannelSort('id')}>
                                    {t('deviceDetail.channels.id')}{getSortIndicator('id')}
                                </button>
                                <button type="button" onClick={() => handleChannelSort('actuator')}>
                                    {t('deviceDetail.channels.actuator')}{getSortIndicator('actuator')}
                                </button>
                                <button type="button" onClick={() => handleChannelSort('type')}>
                                    {t('deviceDetail.channels.type')}{getSortIndicator('type')}
                                </button>
                                <button type="button" onClick={() => handleChannelSort('channel')}>
                                    {t('deviceDetail.channels.channel')}{getSortIndicator('channel')}
                                </button>
                                <button type="button" onClick={() => handleChannelSort('position')}>
                                    {t('deviceDetail.channels.position')}{getSortIndicator('position')}
                                </button>
                                <button type="button" onClick={() => handleChannelSort('connectedAppliances')}>
                                    {t('deviceDetail.channels.applianceCount')}{getSortIndicator('connectedAppliances')}
                                </button>
                            </div>
                            <div className={styles.DeviceDetail__container__appliances__list}>
                                {sortedChannelItems.map((item) => (
                                        <div
                                            key={`${item.actuatorId}-${item.channel}`}
                                            className={styles.DeviceDetail__container__appliances__list__item}
                                            onMouseEnter={() => setHoveredChannel({ actuatorId: item.actuatorId, channel: item.channel })}
                                            onMouseLeave={() => setHoveredChannel(null)}
                                        >
                                            <div
                                                className={styles.DeviceDetail__container__appliances__list__item__main}
                                                draggable
                                                onDragStart={handleChannelDragStart(item)}
                                            >
                                                <img
                                                    className={styles.DeviceDetail__container__appliances__list__item__icon}
                                                    src={item.representativeIcon}
                                                    alt={item.representativeIcon}
                                                    draggable={false}
                                                />
                                                <p>{formatEntityId('actuator', item.actuatorId)}</p>
                                                <p>{item.actuatorType}<br />{item.actuatorName}</p>
                                                <p>{item.applianceType === item.usage ? t(`deviceDetail.applianceTypes.${item.applianceType}`) : (<>{t(`deviceDetail.applianceTypes.${item.applianceType}`)} <br /> {t(`deviceDetail.applianceTypes.${item.usage}`)}</>)}</p>
                                                <p>
                                                    {item.channel}
                                                    {item.numberOfRequiredConnections > 1 && (
                                                        <>
                                                            <br />
                                                            {item.functionLabels.join(' / ')}
                                                        </>
                                                    )}
                                                </p>
                                                <p>{getConnectedPositionsForChannel(item.actuatorId, item.channel)}</p>
                                                <p>{item.applianceIds.length}</p>
                                            </div>
                                            <div className={styles.DeviceDetail__container__appliances__list__item__details}>
                                                <div className={styles.DeviceDetail__container__appliances__list__item__details__items}>
                                                    {item.applianceDetails.map((appliance) => (
                                                        <div
                                                            key={appliance.id}
                                                            className={styles.DeviceDetail__container__appliances__list__item__details__items__item}
                                                        >
                                                            <span>{t('deviceDetail.appliances.id')}: {formatEntityId('appliance', appliance.id)}</span>
                                                            <img draggable='false' src={getIconPath(appliance.icon, '.svg', 'controllers/black')} alt={appliance.label} />
                                                            <span>{appliance.label}</span>
                                                        </div>
                                                    ))}
                                                    {item.applianceDetails.length === 0 && (
                                                        <div className={styles.DeviceDetail__container__appliances__list__item__details__items__empty}>
                                                            <p>{t('deviceDetail.appliances.empty')}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                ))}
                            </div>
                            <div className={styles.DeviceDetail__container__appliances__save}>
                                <Button onClick={() => dispatch(setShowDeviceDetail(false))} size="lg" variant="fill">{t('apply')}</Button>
                            </div>
                        </div>
                        <span></span>
                        <div className={styles.DeviceDetail__container__controller} ref={controllerContainerRef}>
                            <h3>{t('deviceDetail.controllers.heading')}</h3>
                            {getHotelFeatures(controller).length > 0 && (
                                <Button size="sm" variant="fill" onClick={() => setShowHotelFeatures(true)}>{t('hotelFeaturesDetail.open')}</Button>
                            )}
                            <div className={classNames(styles.DeviceDetail__container__controller__content, { [styles.DeviceDetail__container__controller__content__white]: controller.color === 'white' })}>
                                <img src={showControllerIcons ? renderImageName : addPublicURL(`/assets/controllers/render/${controller.renderImage}-${controller.color}.png`)} draggable="false" alt="Controller render" />
                                {controllersRef.find(c => c.name === controller.name)?.hasRoomNumber && (
                                    <div className={styles.DeviceDetail__container__controller__roomNumber}>
                                        <p style={controller.color === 'white' ? { color: 'black' } : undefined} >{new Date().getFullYear()}</p>
                                    </div>
                                )}
                                <div
                                    className={classNames(styles.iconWrapper, styles[controller.className])}
                                    ref={iconWrapperRef}
                                >
                                    {controller.iconsData.map((iconsData, index) => (
                                        <div className={classNames(styles.controllerIcon, styles[`img-${index}`])} key={index}>
                                            <img
                                                key={index}
                                                src={getIconPath(defaultIconsData?.find((slot) => slot.position === iconsData.position)?.icon?.replace(/%c/g, indicatorColor) || 'add_icon')}
                                                alt={iconsData.icon}
                                                draggable={false}
                                                style={(showControllerIcons && !controller.configuredImageName) ? undefined : { visibility: 'hidden' }}
                                                onDragOver={(event) => { setEditingSlotIndex(index); handlePopupDragOver(event); }}
                                                onDrop={handlePopupDrop}
                                            />
                                            {showAppliances && (
                                                <div className={classNames(styles.controllerIcon__appliance)}
                                                    onDragOver={(event) => { setEditingSlotIndex(index); handlePopupDragOver(event); }}
                                                    onDrop={handlePopupDrop}
                                                    onClick={(event) => handleControllerIconClick(event, index)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <img 
                                                        key={index}
                                                        src={getIconPath(`more_${indicatorColor}`)}
                                                        alt="Menu"
                                                        data-menu-trigger="true"
                                                        
                                                    />
                                                    <p style={{ color: (iconsData && iconsData.connectedActuatorChannels && iconsData.connectedActuatorChannels.length > 0) ? indicatorColor : 'red' }}>
                                                        {getSlotConnectedCount(iconsData)}
                                                    </p>
                                                </div>
                                            )}
                                            <div
                                                className={classNames(styles.controllerIcon__position, {
                                                    [styles.controllerIcon__position__highlight]: highlightedControllerSlotIndexes.has(index)
                                                })}
                                                style={{ color: indicatorColor }}
                                            >
                                                <span>{controllerButtonCodes[index] ?? 'A'}</span>
                                            </div>
                                        </div> 
                                    ))}
                                </div>
                            </div>
                            {popupPosition && (
                                <div
                                    ref={popupRef}
                                    className={styles.DeviceDetail__container__controller__appliancePopup}
                                    style={{
                                        top: popupPosition.top,
                                        left: popupPosition.left,
                                    }}
                                    onDragOver={handlePopupDragOver}
                                    onDrop={handlePopupDrop}
                                >
                                    <h4>{t('deviceDetail.controllers.appliancesPopup.heading')}{controllerButtonCodes[editingSlotIndex ?? 0]}</h4>
                                    <span></span>
                                    <div className={styles.DeviceDetail__container__controller__appliancePopup__list}>
                                        {editingSlotChannelInfos.length > 0 ? (
                                            editingSlotChannelInfos.map((channelInfo) => (
                                                <div
                                                    key={`${channelInfo.actuatorId}-${channelInfo.channel}-${channelInfo.functionIndex}`}
                                                    className={styles.DeviceDetail__container__controller__appliancePopup__list__item}
                                                >
                                                    <p>{formatEntityId('actuator', channelInfo.actuatorId)}</p>
                                                    <img src={channelInfo.representativeIcon} alt={channelInfo.actuatorName} />
                                                    <p>{channelInfo.actuatorName}</p>
                                                    <p>{`CH ${channelInfo.channel} (${channelInfo.functionLabel})`}</p>
                                                    <img className={styles.DeviceDetail__container__controller__appliancePopup__list__item__delete}
                                                        src={getIconPath('delete')} alt="Delete" 
                                                        onClick={() => handleChannelUnassign(editingSlotIndex as number, {
                                                            actuatorId: channelInfo.actuatorId,
                                                            channel: channelInfo.channel,
                                                            functionIndex: channelInfo.functionIndex
                                                        })}
                                                    />
                                                </div>
                                            ))
                                        ) : (
                                            <div className={styles.DeviceDetail__container__controller__appliancePopup__empty}>
                                                <p>{t('deviceDetail.controllers.appliancesPopup.empty')}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            <div className={styles.DeviceDetail__container__controller__info}>
                                <p>{controller?.name + (controller?.colorShortcut ?? 'Unknown')} | {formatEntityId('controller', controller?.id)}</p>
                            </div>
                            <div className={styles.DeviceDetail__container__controller__switches}>
                                <div className={styles.DeviceDetail__container__controller__switches__item}>
                                    <Checkbox
                                        checked={showAppliances}
                                        onChange={() => setShowAppliances(prev => !prev)}
                                    ></Checkbox>
                                    <p>{t('deviceDetail.controllers.switches.showAppliances')}</p>
                                </div>
                                <div className={styles.DeviceDetail__container__controller__switches__item}>
                                    <Checkbox
                                        checked={showControllerIcons}
                                        onChange={() => setShowControllerIcons(prev => !prev)}
                                    ></Checkbox>
                                    <p>{t('deviceDetail.controllers.switches.showControllerIcons')}</p>
                                </div>
                            </div>
                            {getAccessToken() && (
                                <div className={styles.DeviceDetail__container__controller__design}>
                                    <h4>{t('deviceDetail.controllers.design')}</h4>
                                    <Button variant='fill' size='lg' onClick={() => handleCreateDesign()}>{t('deviceDetail.controllers.designButton')}</Button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default DeviceDetail;